import { useRequiredParams } from '../../../../utils/hooks';
import { _getJobById, _getJobErrors } from '../../../services/crawler.services';
import './domainLevelStatus.scss';
import { CrawlfishJob, DomainCount, JobError, JobInfoState } from '../../../crawler.interface';
import { JobInfoDefaultState } from '../../../crawler.models';
import Loader from '../../../../components/loader/loader';
import { __sortDomainLevelStatusTable } from '../../../../utils/utils';
import { DownloadArrow } from '../../../../components/icons/icons';
import React from 'react';

export interface DomainStatusTableRow extends DomainCount {
    error: string;
    uri?: string;
}

interface DomainTableState {
    rows: DomainStatusTableRow[];
    sortIndex: number;
    activeColumn: string;
}

const DefaultDomainTableState = {
    rows: [],
    sortIndex: -1,
    activeColumn: ''
};

function DomainLevelStatus() {

    const { id } = useRequiredParams();
    const [jobInfoState, setJobInfo] = React.useState<JobInfoState>(JobInfoDefaultState);
    const [tableData, setTableData] = React.useState<DomainTableState>(DefaultDomainTableState);

    React.useEffect(() => {
        init();
    }, []);

    async function init() {
        try {

            const job: CrawlfishJob | null = await _getJobById(id);
            const allJobErrors: JobError[] = await _getJobErrors(id);

            if (job) {
                setJobInfo({ ...jobInfoState, isLoading: false, info: { ...job.info, domains_failed: allJobErrors.length }, errorMessage: '', job_name: job.name, job_id: job.id });
                const tableRows: DomainStatusTableRow[] = [];
                job?.info?.domain_counts?.forEach((domainCount: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
                    domainCount.error = '';
                    tableRows.push(domainCount);
                });

                if (allJobErrors) {
                    allJobErrors.forEach((jobErr: JobError) => {
                        const matchedDomainFromURI = job.requested_uris.find((uri: string) => jobErr.uri.includes(uri)) || jobErr.domain;
                        tableRows.push({
                            count: 0,
                            domain: matchedDomainFromURI,
                            error: jobErr.message,
                            uri: jobErr.uri
                        });
                    });
                }

                tableRows.sort((a: DomainStatusTableRow, b: DomainStatusTableRow) => b.count > a.count ? 1 : -1);
                setTableData({ ...tableData, rows: tableRows });
            }
        } catch (err) {
            console.error('Failed to initialize job data.');
        }
    }

    function exportTable(): void {
        if (tableData && jobInfoState) {

            let csvContent = 'data:text/csv;charset=utf-8,';
            const csvRows: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
            // Metadata
            csvRows.push(
                ['Job ID', 'Job Name', 'Pages Complete', 'Pages Failed', 'Domains Completed', 'Domains Failed'],
                [
                    jobInfoState.job_id,
                    jobInfoState.job_name,
                    jobInfoState.info.pages_complete || 0,
                    jobInfoState.info.pages_failed || 0,
                    jobInfoState.info.domain_counts?.length || 0,
                    jobInfoState.info.domains_failed || 0,
                ]
            );
            // Header
            csvRows.push(['']); // Empty row for readability
            csvRows.push(['Domain', 'Number of Pages Returned', 'Error Message']);
            const domainsCopy = [...tableData.rows];
            // Sort Lowest to Highest # of Domains
            domainsCopy.sort((a: DomainStatusTableRow, b: DomainStatusTableRow) => b.count > a.count ? 1 : -1);
            domainsCopy.forEach((domain: DomainStatusTableRow) => {
                csvRows.push([domain.domain, domain.count, domain.error]);
            });
            // Add each row to csvContent string
            csvRows.forEach((rowArray: any[]) => {  // eslint-disable-line @typescript-eslint/no-explicit-any
                const row = rowArray.join(",");
                csvContent += row + "\r\n";
            });

            downloadCsvContent(csvContent);
        }
    }

    function downloadCsvContent(csvContent: string): void {
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${jobInfoState.job_name} - Domain Status`);
        document.body.appendChild(link); // Required for FireFox
        link.click();
    }

    function sortByColumn(header: string): void {
        const sortIndex = tableData.sortIndex;

        const rowsToSort = [...tableData.rows];
        const sortedTableRows = __sortDomainLevelStatusTable(header, sortIndex, rowsToSort);
        setTableData({ rows: sortedTableRows, sortIndex: sortIndex * -1, activeColumn: header });
    }

    function setHeaderColumnClass(name: string): string | void {
        if (tableData.sortIndex < 1 && tableData.activeColumn == name) {
            return 'asc';
        } else if (tableData.sortIndex > -1 && tableData.activeColumn == name) {
            return 'desc';
        } else {
            return '';
        }
    }

    return (
        <div className="domains-status-page" data-testid="domain-level-status-page">
            <h1>Status of Domains</h1>
            <div className="metadata-bar domain-level-status expanded" data-cy="domain-level-status-metadata">

                {(jobInfoState.errorMessage.length > 0) ?
                    <span className="message filter-issue">
                        Failed to load data
                    </span>
                    :
                    (jobInfoState?.info?.domain_counts && tableData?.rows && jobInfoState.isLoading == false) ?
                        <ul className="metadata-list no-list-style" data-testid="metadata-header-list">
                            <li className="metadata-item job-id-label">
                                <div className="bold-label mini">JOB ID</div>
                                {jobInfoState?.job_id}
                            </li>
                            <li className="metadata-item job-name-label">
                                <div className="bold-label mini">JOB NAME</div>
                                {jobInfoState?.job_name}
                            </li>
                            <li className="metadata-item pages-completed-label">
                                <div className="bold-label mini">PAGES COMPLETED</div>
                                {jobInfoState?.info?.pages_complete?.toLocaleString()}
                            </li>
                            <li className="metadata-item pages-failed-label">
                                <div className="bold-label mini">PAGES FAILED</div>
                                {jobInfoState?.info?.pages_failed?.toLocaleString()}
                            </li>
                            <li className="metadata-item domain-unique-label" data-testid="unique-domains">
                                <div className="bold-label mini">UNIQUE DOMAINS</div>
                                {jobInfoState?.info?.unique_domains?.toLocaleString()}
                            </li>
                            <li className="metadata-item domain-completed-label" data-testid="domains-completed">
                                <div className="bold-label mini">DOMAINS COMPLETED</div>
                                {jobInfoState?.info?.domain_counts?.length?.toLocaleString()}
                            </li>
                            <li className="metadata-item domain-errored-label" data-testid="domains-failed">
                                <div className="bold-label mini">DOMAINS FAILED</div>
                                {jobInfoState?.info?.domains_failed?.toLocaleString()}
                            </li>
                        </ul>
                        :
                        <Loader loadingText="Loading data ..." spinnerColor="#000" />
                }
            </div>

            <div className="table-info-section export-domains">
                <div className="total-results" data-testid="total-results">
                    Total: {jobInfoState?.info.domain_counts?.length}
                </div>
                <a className="export-domains-link" onClick={exportTable} data-cy="export-button">
                    <DownloadArrow />
                    <span>Export</span>
                </a>
            </div>
            <div className="domains-table-wrapper">
                <div className="domains-table" data-cy="domains-table">
                    <div className="index-header">
                        <a className={`index-header-category domain sort ` + (setHeaderColumnClass('domain'))} onClick={() => sortByColumn('domain')}>
                            Domain
                        </a>
                        <a className={`index-header-category count sort ` + (setHeaderColumnClass('count'))} onClick={() => sortByColumn('count')}>
                            Number of Pages Returned
                        </a>
                        <a className={`index-header-category error sort ` + (setHeaderColumnClass('error'))} onClick={() => sortByColumn('error')}>
                            Error Message
                        </a>
                    </div>
                    <div className="main-table-content">
                        {(jobInfoState.errorMessage.length > 0) ?
                            <span className="message filter-issue">
                                {jobInfoState.errorMessage}
                            </span>
                            :
                            (tableData && jobInfoState.isLoading == false) ?
                                tableData.rows.map((row: DomainStatusTableRow) => (
                                    <div className={"index-row" + (row.error.length > 0 ? " index--failed" : "")}
                                        key={row.domain + row.uri + row.count}
                                        data-testid={"index-row"}
                                        data-isfailed={row.error.length > 0 ? true : false}>
                                        <div className="index-row-item domain-name" data-testid="domain-name">
                                            {row.domain ? row.domain : row?.uri || 'No domain or URI found'}
                                        </div>
                                        <div className="index-row-item domain-count" data-testid="domain-count">{row.count?.toLocaleString()}</div>
                                        <div className="index-row-item domain-error" data-testid="domain-error">{row.error}</div>
                                    </div>
                                ))
                                :
                                <Loader loadingText="Loading data ..." spinnerColor="#000" />
                        }
                    </div>
                </div>
            </div>
        </div >
    );
}
export default DomainLevelStatus;